import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import ThemeSwitcher from './ThemeSwitcher';
import ReportSearch from './ReportSearch';
import DropdownMenu from './DropdownMenu';
import { ThemeContext } from '../Context';
import { ReactComponent as BlueNoteLogo } from '../../assets/logo_bluenote.svg';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  DialogContentText, 
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

function Header() {
  const { theme } = useContext(ThemeContext);
  const muiTheme = useTheme();
  const isPilotBadgeCompact = useMediaQuery(muiTheme.breakpoints.down('lg'));
  const [isPilot, setIsPilot] = useState(false);
  const [isPilotDialogOpen, setIsPilotDialogOpen] = useState(false);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const isDark = theme === 'dark';

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    // Observer les changements de classe sur le body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen(); // Vérification initiale

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchIsPilot = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (!mounted) return;
        setIsPilot(Boolean(response?.data?.isPilot));
      } catch (error) {
        console.error('Erreur lors de la récupération de isPilot :', error);
      }
    };

    fetchIsPilot();
    return () => {
      mounted = false;
    };
  }, []);

  const handlePilotFeedback = () => {
    try {
      window.open('https://pyll.be', '_blank', 'noopener');
    } catch (error) {
      console.error('Impossible d\'ouvrir le formulaire de feedback :', error);
    }
  };
  
  // Ne pas afficher le header quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  return (
    <Box 
      className={theme}
      sx={{
        height: '6.5vh',
        minHeight: '48px',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        px: { xs: 1, sm: 1.5, md: '18px' },
        background: isDark 
          ? 'linear-gradient(to right,rgb(37, 53, 100),rgb(52, 74, 141))'
          : 'linear-gradient(to right,#1a253c, #2d407a)',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 1200
      }}
    >
      <Box 
        component="header"
        sx={{
          display: 'grid',
          // La recherche reste centrée tant que la place le permet ; la colonne de droite
          // ne rétrécit jamais sous son contenu, c'est la gauche qui cède en premier.
          gridTemplateColumns: {
            xs: 'auto minmax(0, 1fr) auto',
            sm: 'minmax(0, 1fr) auto minmax(max-content, 1fr)'
          },
          alignItems: 'center',
          columnGap: { xs: 1, sm: 1.5 },
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Left Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          alignSelf: 'stretch',
          gap: { xs: 1, md: '12px' },
          minWidth: 0,
          overflow: 'hidden'
        }}>
          <Box
            component={BlueNoteLogo}
            className='logo-bluenote'
            sx={{
              height: { xs: '32px', sm: '42px' },
              flexShrink: 0,
              width: 'auto',
              color: 'white'
            }}
          />
          <Box
            component="h1"
            sx={{
              display: { xs: 'none', md: 'block' },
              color: 'white',
              fontSize: { md: '1.6rem', lg: '2rem' },
              margin: 0,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            BlueNote
          </Box>
          <Box
            component="p"
            sx={{
              display: { xs: 'none', md: 'block' },
              color: 'white',
              fontStyle: 'italic',
              fontSize: '0.95rem',
              margin: 0,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            <SettingsFieldDisplay fieldName="clientName" />
          </Box>
          {isPilot && (
            <Tooltip title={isPilotBadgeCompact ? 'Version pilote : avis, retour, bug ?' : ''} arrow>
              <Box
                role="button"
                tabIndex={0}
                aria-label="Version pilote : avis, retour, bug ?"
                onClick={() => setIsPilotDialogOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsPilotDialogOpen(true);
                  }
                }}
                sx={{
                  backgroundColor: '#b71c1c',
                  color: '#ffffff',
                  borderRadius: '999px',
                  px: { xs: 0.75, md: 1.5 },
                  py: { xs: 0.75, md: 0.5 },
                  ml: 'auto',
                  flexShrink: 0,
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  lineHeight: 1.15,
                  cursor: 'pointer',
                  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.25)',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: '1.2rem', color: '#ffffff' }} />
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', lineHeight: 1.1 }}>
                  <Box component="div" sx={{ fontSize: '0.9rem' }}>
                    <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>Version </Box>
                    Pilote
                  </Box>
                  <Box component="div" sx={{ display: { xs: 'none', lg: 'block' }, fontSize: '0.75rem', fontWeight: 600 }}>
                    Avis, retour, bug ?
                  </Box>
                </Box>
              </Box>
            </Tooltip>
          )}
        </Box>
        
        {/* Center Section */}
        <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <ReportSearch />
        </Box>
        
        {/* Right Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          gap: { xs: 0.5, sm: '12px' }
        }}>
          <DropdownMenu />
          <ThemeSwitcher />
        </Box>
      </Box>

      <Dialog
        open={isPilotDialogOpen}
        onClose={() => setIsPilotDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Version pilote</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              BlueNote fonctionne actuellement en mode pilote pour votre zone.
            </Typography>
            <Typography variant="body2">
            BlueNote est actuellement déployé en version pilote pour votre zone, dans une logique d’amélioration continue.
            <br />
            Les contenus générés doivent toujours être relus et validés par l’utilisateur, en particulier ceux issus de l’assistant d'intelligence artificielle « Guardia », utilisé comme aide à la rédaction.
            <br />
            Nous vous invitons à signaler les fautes d’orthographe, options manquantes dans une rubrique ou anomalies de génération via le formulaire de feedback.
            <br />
            Chaque retour contribue à la stabilisation de la version finale.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handlePilotFeedback}
          >
            Formulaire de feedback
          </Button>
          <Button onClick={() => setIsPilotDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Header;